/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { useToast } from "@/shared/hooks/use-toast";
import { Icons } from "@/shared/components/icons/icons";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Link } from "react-router-dom";
import AuthLoading from "./AuthLoading";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const { signIn, isAuthenticating } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Check if social sign-in is disabled via environment variable
  // Using window.ENV for runtime configuration (Azure Container Apps)
  // Falls back to import.meta.env for local development
  const isSocialSignInDisabled = 
    ((window as any).ENV?.VITE_DISABLE_SOCIAL_SIGNIN && (window as any).ENV.VITE_DISABLE_SOCIAL_SIGNIN !== '%%VITE_DISABLE_SOCIAL_SIGNIN%%')
      ? (window as any).ENV.VITE_DISABLE_SOCIAL_SIGNIN === 'true'
      : import.meta.env.VITE_DISABLE_SOCIAL_SIGNIN === 'true';
  
  // If we're still checking authentication status, show the loading screen
  // This prevents the login page flash when a user is already authenticated
  if (isAuthenticating) {
    return <AuthLoading />;
  }

  /**
   * Destino do `?redirect=` da URL, usado pelo aceite de convite de quem já tem conta
   * (AB#84509): sem isto o login joga a pessoa na home e o convite se perde.
   *
   * Só caminho interno é aceito. Sem essa checagem, um link de login com redirect
   * para fora vira ferramenta de phishing: a vítima entra numa tela legítima do
   * Prodgy e é despejada em domínio de terceiro logo após autenticar.
   */
  const safeRedirectTarget = (): string | null => {
    const target = new URLSearchParams(window.location.search).get("redirect");
    if (!target) return null;
    if (!target.startsWith("/") || target.startsWith("//")) return null;
    return target;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        variant: "destructive",
        title: t("auth.login.error"),
        description: t("auth.login.missingCredentials"),
      });
      return;
    }

    setIsLoading(true);
    try {
      console.log("Attempting to sign in with email:", email);
      await signIn("email", email, password);
      // Se chegou aqui, não é onboarding redirect - redirecionar para home
      navigate(safeRedirectTarget() ?? "/");
    } catch (error: any) {
      console.error("Login error:", error);
      // Se for onboarding redirect, não fazer nada (o AuthContext já redirecionou)
      // Para outros erros, o toast já foi exibido no AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    // Aguardar um momento para garantir que o React renderize o spinner
    // antes de chamar signIn que fará o redirect
    await new Promise(resolve => setTimeout(resolve, 50));
    try {
      await signIn("google");
    } catch (error: any) {
      console.error("Google sign in error:", error);
      setIsGoogleLoading(false);
    }
    // Não resetar loading aqui porque o redirect acontece
  };

  const handleMicrosoftSignIn = async () => {
    setIsMicrosoftLoading(true);
    // Aguardar um momento para garantir que o React renderize o spinner
    // antes de chamar signIn que fará o redirect
    await new Promise(resolve => setTimeout(resolve, 50));
    try {
      await signIn("azure");
    } catch (error: any) {
      console.error("Microsoft sign in error:", error);
      setIsMicrosoftLoading(false);
    }
    // Não resetar loading aqui porque o redirect acontece
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-4 animate-fade-in">
        <div className="flex justify-center">
          <img 
            src="/assets/logo.png" 
            alt="Pro Logo" 
            className="h-32 w-auto mb-2"
          />
        </div>
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-3 pb-8">
            <CardTitle className="text-2xl font-medium tracking-tight text-center">
              {t("auth.login.title")}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {t("auth.login.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <Input
                  type="email"
                  placeholder={t("auth.login.email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="h-11"
                  required
                />
                <Input
                  type="password"
                  placeholder={t("auth.login.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="h-11"
                  required
                />
              </div>

              <div className="flex flex-col gap-6">
                <Button 
                  type="submit" 
                  className="w-full font-normal h-11 relative"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner className="h-5 w-5 text-white" size="sm" />
                    </div>
                  ) : (
                    t("auth.login.signIn")
                  )}
                </Button>

                {!isSocialSignInDisabled && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          {t("auth.login.orContinueWith")}
                        </span>
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <Button
                        variant="outline"
                        className="w-full font-normal h-11"
                        onClick={handleGoogleSignIn}
                        disabled={isLoading || isGoogleLoading || isMicrosoftLoading}
                        type="button"
                      >
                        {isGoogleLoading ? (
                          <LoadingSpinner className="h-5 w-5" size="sm" />
                        ) : (
                          <>
                            <Icons.google className="mr-2 h-5 w-5" />
                            {t("auth.login.continueWithGoogle")}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full font-normal h-11"
                        onClick={handleMicrosoftSignIn}
                        disabled={isLoading || isGoogleLoading || isMicrosoftLoading}
                        type="button"
                      >
                        {isMicrosoftLoading ? (
                          <LoadingSpinner className="h-5 w-5" size="sm" />
                        ) : (
                          <>
                            <Icons.microsoft className="mr-2 h-5 w-5" />
                            {t("auth.login.continueWithMicrosoft")}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}

                <div className="flex justify-center gap-8 pt-4 text-sm">
                  <Link 
                    to="/recover-password"
                    className="text-muted-foreground hover:text-muted-foreground/80 transition-colors underline underline-offset-4"
                  >
                    {t("auth.login.forgotPassword")}
                  </Link>
                  <Link
                    to="/signup"
                    className="text-muted-foreground hover:text-muted-foreground/80 transition-colors underline underline-offset-4"
                  >
                    {t("auth.login.createAccount")}
                  </Link>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      {/* Powered by footer */}
      <div className="absolute bottom-4 right-4">
        <span className="text-sm text-muted-foreground opacity-80">
          {t("common.poweredBy")}{' '}
          <a 
            href="https://programmers.com.br" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-muted-foreground/80 transition-colors"
          >
            {t("common.companyName")}
          </a>
          {t("common.trademark")}
        </span>
      </div>
    </div>
  );
}
